import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { ISemester } from '../../../models/semesters.model';
import { doGetCourses } from '../../../store/class.slice';
import { doRemoveAuth } from '../../../store/auth.slice';
import { RootState } from '../../../store/rootReducer';

import { useSnackbar } from 'notistack';
import { Select } from 'antd';
import Button from '../../Button';
import Loader from '../../Loader';
import {
  infoVariant,
  warningVariant,
  errorVariant,
} from '../../../lib/barVariant';
import { IClassInfoReq2, ICourse } from '../../../models/campusClasses.model';
import SelectTeacherModal from '../../modals/SelectTeacherModal';
import { IStaff } from '../../../models/employees.model';
import { doGetCampusClassrooms } from '../../../store/classroom.slice';
import ClassTimetable from './Timetable';
import QueryString from 'qs';
import useAuth from '../../../hooks/useAuth';
import useNotification from '../../../hooks/useNotification';

const { Option } = Select;

interface IProps {
  classData?: any;
  submit: any;
}

const ClassEditor: FC<IProps> = ({ classData, submit }) => {
  const { writeAuthYn } = useAuth();
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { infoNotification } = useNotification();
  const history = useHistory();
  const location = useLocation();
  const query = QueryString.parse(location.search, { ignoreQueryPrefix: true });
  const dispatch = useDispatch();
  const { currentCampus, currentSemesterCode } = useSelector(
    (state: RootState) => state.auth,
  );
  // 공통 코드
  const { commonCodes } = useSelector((state: RootState) => state.commonCode);
  // CL0002 = 수업시간(교시당)
  const CL0002 = commonCodes.find(
    (item) => item.commonCode === 'CL0002',
  )?.detailCode;
  // SY0001 = 요일
  const SY0001 = commonCodes.find(
    (item) => item.commonCode === 'SY0001',
  )?.detailCode;
  // 학기 목록
  const { semesters, isLoading: semesterLoading } = useSelector(
    (state: RootState) => state.semester,
  );
  // 과정 목록
  const { courses, isLoading: courseLoading } = useSelector(
    (state: RootState) => state.class,
  );
  // 강의실 목록
  const { classrooms, isLoading: classroomLoading } = useSelector(
    (state: RootState) => state.classroom,
  );

  const [data, setData] = useState<any>();
  // 캠퍼스
  const [campus, setCampus] = useState<string>();
  // 학기
  const [semester, setSemester] = useState('');
  // 학급명
  const [className, setClassName] = useState('');
  // 과정
  const [course, setCourse] = useState<string>();
  // 과정 정보
  const [courseInfo, setCourseInfo] = useState<any>();
  // 수업시간(교시)
  const [lessonTime, setLessonTime] = useState<string>();
  // 분당 단가
  const [minPrice, setMinPrice] = useState('');
  // 강사 배정 모달 표시여부
  const [selectTeacherVisible, setSelectTeacherVisible] = useState(false);
  // 강사 모달을 호출한 input 정보
  const [teacherInputRef, setTeacherInputRef] = useState<any>();
  // 담임 강사
  const [teacher, setTeacher] = useState<any>();
  // 월 수업료
  const [monthTuition1, setMonthTuition1] = useState('');
  const [monthTuition2, setMonthTuition2] = useState('');
  const [monthTuition3, setMonthTuition3] = useState('');
  // 수업시간
  const [scheduleInput, setScheduleInput] = useState<any>([]);
  // 요일 목록
  const [dayList, setDayList] = useState<any>([]);
  // 강의실 목록
  const [classroomList, setClassroomList] = useState<any>([]);
  // 학생 배정 여부
  const [regYn, setRegYn] = useState('N');

  // 과정 선택 시 해당 과정의 정보를 찾아서 state에 넣는다
  useEffect(() => {
    if (course) {
      const currentCourse = courses?.find(
        (item) => item.semesterCourseCode === Number(course),
      );
      setCourseInfo(currentCourse);
    }
  }, [course, courses]);

  // 요일 목록을 만든다
  useEffect(() => {
    if (SY0001) {
      const list: any = [];
      SY0001.forEach((item: any) => {
        list.push({
          label: item.korName,
          value: item.detailCode,
        });
      });
      // 주말(토일) 제외
      list.splice(6, 1);
      list.splice(0, 1);
      setDayList(list);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [commonCodes]);

  // 강의실 목록을 만든다
  useEffect(() => {
    if (classrooms) {
      const _classrooms: any = [];
      classrooms[0]?.classroom.forEach((item: any) =>
        _classrooms.push({
          label: item.classroomName,
          value: item.classroomCode,
        }),
      );
      setClassroomList(_classrooms);
    }
  }, [classrooms]);

  useEffect(() => {
    if (data) {
      if (!currentSemesterCode) {
        getCourses(data.semesterCode);
      }
      getClassrooms(data.campusCode);
      setCampus(data.campusCode);
      setSemester(data.semesterCode);
      setClassName(data.className);
      setCourse(data.semesterCourseCode);
      setLessonTime(data.lessonTime);
      setMinPrice(data.minPrice);
      setTeacher({
        memberCode: data.teacherCode,
        memberName: data.teacherKorName,
      });
      setMonthTuition1(data.tuition[0].monthTuition);
      setMonthTuition2(data.tuition[1].monthTuition);
      setMonthTuition3(data.tuition[2].monthTuition);
      setRegYn(data.regYn);

      const input = data.classInfo.map((item: any) => {
        return {
          day: item.lessonWeekday,
          classes: item.classTime.map((subitem: any) => {
            return {
              classTimeCode: subitem.classTimeCode,
              startTime: subitem.srtTime,
              classroom: subitem.classRoomCode,
              teacher: {
                memberCode: subitem.teacherCode,
                memberName: subitem.teacherKorName,
              },
            };
          }),
        };
      });

      setScheduleInput(input);
    } else if (data === null) {
      getClassrooms(currentCampus);
      setCampus(currentCampus);
      setSemester('');
      setClassName('');
      setCourse(undefined);
      setCourseInfo(undefined);
      setLessonTime(undefined);
      setMinPrice('');
      setTeacher(undefined);
      setMonthTuition1('');
      setMonthTuition2('');
      setMonthTuition3('');
      setRegYn('N');
      setScheduleInput([]);
    }
  }, [data]);

  useEffect(() => {
    if (query.tab === 'createClass') {
      setData(null);
    } else {
      setData(classData);
    }
  }, [classData, query.tab]);

  useEffect(() => {
    setCampus(currentCampus);
  }, [currentCampus]);

  useEffect(() => {
    if (currentSemesterCode) {
      setSemester(String(currentSemesterCode));
      getCourses(currentSemesterCode);
    }
  }, [currentSemesterCode]);

  // 과정 목록을 가져온다
  const getCourses = async (semesterCode: any) => {
    const res = await dispatch(doGetCourses(semesterCode));

    switch (res?.status) {
      case 200:
        break;
      case 401:
        dispatch(doRemoveAuth());
        break;
      case 403:
        enqueueSnackbar(t('common.message.forbidden'), warningVariant);
        history.push('/');
        break;
      default:
        enqueueSnackbar(
          res?.data?.data || t('common.message.error'),
          errorVariant,
        );
    }
  };

  // (특정 캠퍼스의) 강의실 목록을 가져온다
  const getClassrooms = async (campusCode: string) => {
    const res = await dispatch(doGetCampusClassrooms(campusCode));

    switch (res?.status) {
      case 200:
        break;
      case 401:
        dispatch(doRemoveAuth());
        break;
      case 403:
        enqueueSnackbar(t('common.message.forbidden'), warningVariant);
        history.push('/');
        break;
      default:
        enqueueSnackbar(
          res?.data?.data || t('common.message.error'),
          errorVariant,
        );
    }
  };

  // 강사 배정 모달을 연다
  const onOpenSelectTeacher = () => {
    if (!campus) {
      return enqueueSnackbar(t('class.class.message2'), infoVariant);
    }

    setSelectTeacherVisible(true);
  };

  // 월수업료를 자동 계산한다
  const calculateTuition = (
    course: string,
    lessonTime: string,
    minPrice: string,
  ) => {
    const _course: any = courses?.find(
      (item: any) => item.semesterCourseCode === Number(course),
    );
    if (!_course) return;

    const _lessonTime = Number(lessonTime);
    const _minPrice = Number(minPrice);

    const _monthTuition1 =
      _lessonTime *
      _minPrice *
      _course?.dayLessonCount *
      _course?.months[0].lessonCount;
    const _monthTuition2 =
      _lessonTime *
      _minPrice *
      _course?.dayLessonCount *
      _course?.months[1].lessonCount;
    const _monthTuition3 =
      _lessonTime *
      _minPrice *
      _course?.dayLessonCount *
      _course?.months[2].lessonCount;

    setMonthTuition1(String(_monthTuition1));
    setMonthTuition2(String(_monthTuition2));
    setMonthTuition3(String(_monthTuition3));
  };

  // ScheduleInput 입력 함수
  const onSetScheduleInput = (index: number, key: string, value: string) => {
    const _scheduleInput = [...scheduleInput];
    _scheduleInput[index][key] = value;
    setScheduleInput(_scheduleInput);
  };

  // 시작시간 입력 함수
  const onSetStartTime = (i1: number, i2: number, value: string) => {
    const _scheduleInput = [...scheduleInput];
    _scheduleInput[i1].classes[i2].startTime = value;
    setScheduleInput(_scheduleInput);
  };

  // ScheduleInput.classes 입력 함수
  const onSetScheduleClassesInput = (
    i1: number,
    i2: number,
    key: string,
    value: any,
  ) => {
    const _scheduleInput = [...scheduleInput];
    _scheduleInput[i1].classes[i2][key] = value;
    setScheduleInput(_scheduleInput);
  };

  // teacher 입력 함수
  const onSetTeacher = (teacher: IStaff) => {
    if (teacherInputRef === 'classTeacher') {
      setTeacher(teacher);
    } else {
      const i1 = teacherInputRef.index1;
      const i2 = teacherInputRef.index2;
      const _scheduleInput = [...scheduleInput];
      _scheduleInput[i1].classes[i2].teacher = teacher;
      setScheduleInput(_scheduleInput);
    }
  };

  // 수업시간 입력 초기화
  const initScheduleInput = (courseCode: string) => {
    const currentCourse = courses?.find(
      (item) => item.semesterCourseCode === Number(courseCode),
    );

    const weekLectureCount = currentCourse?.weekLectureCount;
    const dayLessonCount = currentCourse?.dayLessonCount;
    const input = [];

    if (weekLectureCount && dayLessonCount) {
      for (let i = 0; i < weekLectureCount; i++) {
        const classes = [];
        for (let j = 0; j < dayLessonCount; j++) {
          classes.push({ startTime: null, classroom: '', teacher: null });
        }

        let day;
        switch (String(weekLectureCount) + i) {
          case '20':
            day = 'TU';
            break;
          case '21':
            day = 'TH';
            break;
          case '30':
            day = 'MO';
            break;
          case '31':
            day = 'WE';
            break;
          case '32':
            day = 'FR';
            break;
          case '50':
            day = 'MO';
            break;
          case '51':
            day = 'TU';
            break;
          case '52':
            day = 'WE';
            break;
          case '53':
            day = 'TH';
            break;
          case '54':
            day = 'FR';
            break;
        }

        input.push({ day, classes });
      }
    }

    setScheduleInput(input);
  };

  // 기본정보 테이블 유효성 검사
  const isBasicInfoValidated = () => {
    if (!campus) {
      enqueueSnackbar(t('class.class.message3'), infoVariant);
      return false;
    }
    if (!semester) {
      enqueueSnackbar(t('class.class.message4'), infoVariant);
      return false;
    }
    if (!className) {
      enqueueSnackbar(t('class.class.message5'), infoVariant);
      return false;
    }
    if (!course) {
      enqueueSnackbar(t('class.class.message6'), infoVariant);
      return false;
    }
    if (!lessonTime) {
      enqueueSnackbar(t('class.class.message7'), infoVariant);
      return false;
    }
    if (!minPrice) {
      enqueueSnackbar(t('class.class.message8'), infoVariant);
      return false;
    }
    if (!teacher) {
      enqueueSnackbar(t('class.class.message9'), infoVariant);
      return false;
    }
    if (!monthTuition1 || !monthTuition2 || !monthTuition3) {
      infoNotification(t('class.class.message10'));
      return false;
    }
    if (
      Number(minPrice) <= 0 ||
      Number(monthTuition1) <= 0 ||
      Number(monthTuition2) <= 0 ||
      Number(monthTuition3) <= 0
    ) {
      infoNotification(t('class.class.message11'));
      return false;
    }

    return true;
  };

  // 수업시간 테이블 유효성 검사
  const isScheduleValidated = () => {
    for (let i = 0; i < scheduleInput.length; i++) {
      const schedule = scheduleInput[i];
      if (!schedule.day) {
        enqueueSnackbar(t('class.class.message12'), infoVariant);
        return false;
      }
      for (let j = 0; j < schedule.classes.length; j++) {
        const classItem = schedule.classes[j];
        if (!classItem.startTime || !classItem.classroom) {
          enqueueSnackbar(
            `${t('class.class.message13')}(${schedule.day} ${j + 1}) ${t(
              'class.class.message14',
            )}`,
            infoVariant,
          );
          return false;
        }
        if (!classItem.teacher) {
          enqueueSnackbar(
            `${t('class.class.message15')}(${schedule.day} ${j + 1}) ${t(
              'class.class.message16',
            )}`,
            infoVariant,
          );
          return false;
        }
      }
    }

    return true;
  };

  // 신규학급 등록 함수
  const onSubmit = async () => {
    if (!isBasicInfoValidated() || !isScheduleValidated()) return;

    const req: any = {
      className,
      semesterCourseCode: course,
      lessonTime,
      minPrice,
      teacherCode: teacher?.memberCode,
      tuition: [
        {
          monthSeq: 1,
          monthTuition: monthTuition1,
        },
        {
          monthSeq: 2,
          monthTuition: monthTuition2,
        },
        {
          monthSeq: 3,
          monthTuition: monthTuition3,
        },
      ],
      classInfo: [],
    };

    scheduleInput.forEach((schedule: any) => {
      const classTime: any = [];
      schedule.classes.forEach((classItem: any, index: number) => {
        classTime.push({
          classTimeSeq: index + 1,
          srtTime: classItem?.startTime,
          teacherCode: classItem?.teacher?.memberCode,
          classRoomCode: classItem?.classroom,
        });
      });
      req.classInfo.push({ lessonWeekday: schedule.day, classTime });
    });

    submit(campus, req);
  };

  // 학급 수정 함수 (기본정보)
  const onSubmit1 = async () => {
    if (!isBasicInfoValidated()) return;

    const req: any = {
      className,
      semesterCourseCode: course,
      lessonTime,
      minPrice,
      teacherCode: teacher.memberCode,
      tuition: [
        {
          monthSeq: 1,
          monthTuition: monthTuition1,
        },
        {
          monthSeq: 2,
          monthTuition: monthTuition2,
        },
        {
          monthSeq: 3,
          monthTuition: monthTuition3,
        },
      ],
    };

    submit(1, data.classCode, req);
  };

  // 학급 수정 함수 (수업시간)
  const onSubmit2 = async () => {
    if (!isScheduleValidated()) return;

    const req: IClassInfoReq2[] = [];

    scheduleInput.forEach((schedule: any) => {
      schedule.classes.forEach((classItem: any) => {
        req.push({
          classTimeCode: classItem.classTimeCode,
          srtTime: classItem.startTime,
          teacherCode: classItem.teacher.memberCode,
          classRoomCode: classItem.classroom,
        });
      });
    });

    submit(2, data.classCode, req);
  };

  return (
    <>
      {semesterLoading || courseLoading || classroomLoading ? <Loader /> : null}
      <div className="text-left mt-2">
        <h5>{t('class.class.title2')}</h5>
      </div>
      <table className="table-custom">
        <colgroup>
          <col width="25%" />
          <col width="25%" />
          <col width="25%" />
          <col width="25%" />
        </colgroup>
        <tbody>
          <tr>
            <th>{t('class.class.column10')}</th>
            <td>
              <input
                type="text"
                className="form-control"
                placeholder={t('common.text.input')}
                value={className}
                onChange={(e) => setClassName(e.target.value)}
              />
            </td>
            <th>{t('class.class.column26')}</th>
            <td>
              <Select
                className="w-100"
                placeholder={t('common.button.select')}
                value={semester ? String(semester) : undefined}
                onChange={(value) => {
                  setSemester(value);
                  getCourses(value);
                }}
                disabled={regYn === 'Y' || Boolean(classData)}
              >
                {semesters?.map((item: ISemester) => {
                  return (
                    <Option
                      key={item.semesterCode}
                      value={String(item.semesterCode) || ''}
                    >
                      {item.semesterName}
                    </Option>
                  );
                })}
              </Select>
            </td>
          </tr>
          <tr>
            <th>{t('class.class.column19')}</th>
            <td>
              <Button
                className="btn btn-sm btn-success btn-w55p mr-1"
                onClick={() => {
                  setTeacherInputRef('classTeacher');
                  onOpenSelectTeacher();
                }}
                text={t('common.button.select')}
              />
              {teacher?.memberName}
            </td>
            <th>{t('class.class.column11')}</th>
            <td>
              <Select
                className="w-100"
                placeholder={t('common.button.select')}
                value={course}
                onChange={(value) => {
                  setCourse(value);
                  initScheduleInput(value);
                  if (minPrice && lessonTime) {
                    calculateTuition(value, lessonTime, minPrice);
                  }
                }}
                disabled={
                  !(courses && courses.length > 0) ||
                  regYn === 'Y' ||
                  Boolean(classData)
                }
              >
                {courses?.map((item: ICourse) => {
                  return (
                    <Option
                      key={item.semesterCourseCode}
                      value={String(item.semesterCourseCode) || ''}
                    >
                      {item.courseDetailName}
                    </Option>
                  );
                })}
              </Select>
            </td>
          </tr>
          <tr>
            <th>{t('class.class.column12')}</th>
            <td>{courseInfo?.courseName}</td>
            <th>{t('class.class.column13')}</th>
            <td>{courseInfo?.courseSubName}</td>
          </tr>
          <tr>
            <th>{t('class.class.column14')}</th>
            <td>
              <Select
                className="w-100p"
                placeholder={t('class.class.placeholder5')}
                disabled={regYn === 'Y'}
                value={lessonTime}
                onChange={(value) => {
                  setLessonTime(value);
                  if (course && minPrice) {
                    calculateTuition(course, value, minPrice);
                  }
                }}
              >
                {CL0002?.map((item: any) => {
                  return (
                    <Option key={item.detailCode} value={item.detailCode}>
                      {item.korName}
                    </Option>
                  );
                })}
              </Select>
              &nbsp;{t('class.class.text7')}
            </td>
            <th>{t('class.class.column15')}</th>
            <td>
              {courseInfo
                ? courseInfo.dayLessonCount + ` ${t('class.class.text8')}`
                : ``}
            </td>
          </tr>
          <tr>
            <th>{t('class.class.column16')}</th>
            <td>
              {courseInfo
                ? courseInfo.weekLectureCount + ` ${t('class.class.text2')}`
                : ``}
            </td>
            <th>{t('class.class.column105')}</th>
            <td>
              <input
                type="number"
                className="form-control"
                placeholder={t('common.text.input')}
                disabled={regYn === 'Y'}
                value={minPrice}
                onChange={(e) => {
                  setMinPrice(e.target.value);
                  if (course && lessonTime) {
                    calculateTuition(course, lessonTime, e.target.value);
                  }
                }}
              />
            </td>
          </tr>
          <tr>
            <th>{t('payment.charge.option3')}</th>
            <td>
              {courseInfo?.onlineTuition?.toLocaleString() || 0}{' '}
              {t('class.class.text6')}
            </td>
            <th>{t('payment.charge.option4')}</th>
            <td>
              {courseInfo?.alexTuition?.toLocaleString() || 0}{' '}
              {t('class.class.text6')}
            </td>
          </tr>
          <tr>
            <th rowSpan={3}>{t('payment.charge.option2')}</th>
            <td colSpan={3}>
              <div className="flex">
                <span className="pt-2">
                  {t('class.class.text1')}
                  {courseInfo
                    ? `(${courseInfo.months[0].lessonCount}${t(
                        'class.class.text2',
                      )})`
                    : ''}
                </span>
                <input
                  type="number"
                  className="form-control w-150p ml-2"
                  disabled={regYn === 'Y'}
                  value={monthTuition1}
                  onChange={(e) => setMonthTuition1(e.target.value)}
                />
              </div>
            </td>
          </tr>
          <tr>
            <td colSpan={3}>
              <div className="flex">
                <span className="pt-2">
                  {t('class.class.text3')}
                  {courseInfo
                    ? `(${courseInfo.months[1].lessonCount}${t(
                        'class.class.text2',
                      )})`
                    : ''}
                </span>
                <input
                  type="number"
                  className="form-control w-150p ml-2"
                  disabled={regYn === 'Y'}
                  value={monthTuition2}
                  onChange={(e) => setMonthTuition2(e.target.value)}
                />
              </div>
            </td>
          </tr>
          <tr>
            <td colSpan={3}>
              <div className="flex">
                <span className="pt-2">
                  {t('class.class.text4')}
                  {courseInfo
                    ? `(${courseInfo.months[2].lessonCount}${t(
                        'class.class.text2',
                      )})`
                    : ''}
                </span>
                <input
                  type="number"
                  className="form-control w-150p ml-2"
                  disabled={regYn === 'Y'}
                  value={monthTuition3}
                  onChange={(e) => setMonthTuition3(e.target.value)}
                />
              </div>
            </td>
          </tr>
        </tbody>
      </table>
      {data ? (
        <div className="text-right mt-4 mb-4">
          <Button
            className="btn btn-sm btn-primary btn-w100p"
            onClick={onSubmit1}
            text={t('common.button.save')}
            writeAuthYn={writeAuthYn}
          />
        </div>
      ) : null}

      <ClassTimetable
        courseInfo={courseInfo}
        scheduleInput={scheduleInput}
        onSetScheduleInput={onSetScheduleInput}
        dayList={dayList}
        onSetStartTime={onSetStartTime}
        onSetScheduleClassesInput={onSetScheduleClassesInput}
        classroomList={classroomList}
        campus={campus}
        setTeacherInputRef={setTeacherInputRef}
        onOpenSelectTeacher={onOpenSelectTeacher}
        regYn={regYn}
      />
      {regYn === 'N' ? (
        <div className="text-right mt-4">
          <Button
            className="btn btn-sm btn-primary btn-w100p"
            onClick={data ? onSubmit2 : onSubmit}
            text={t('common.button.save')}
            writeAuthYn={writeAuthYn}
          />
        </div>
      ) : null}

      {/* 강사 배정 모달 */}
      {selectTeacherVisible && (
        <SelectTeacherModal
          visible={selectTeacherVisible}
          setVisible={setSelectTeacherVisible}
          campusCode={campus}
          setTeacher={onSetTeacher}
        />
      )}
    </>
  );
};

export default ClassEditor;
